import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';

const DELIVERY_THRESHOLD = 500;
const DELIVERY_FEE       = 49;

const Stepper = ({ value, onDecrement, onIncrement }) => (
  <div className="stepper">
    <button className="stepper-btn stepper-btn-dec" onClick={onDecrement}>−</button>
    <div className="stepper-value">{value}</div>
    <button className="stepper-btn stepper-btn-inc" onClick={onIncrement}>+</button>
  </div>
);

const CartItem = ({ item, onUpdateQuantity, onRemove }) => (
  <div className="cart-item-row">
    <div className="emoji-box">{item.emoji}</div>
    <div className="flex-1-min0">
      <div className="flex-row-gap7">
        <div className="cart-item-name">{item.name}</div>
        {item.prescription && <span className="badge-rx">Rx</span>}
      </div>
      <div className="cart-item-meta">{item.brand} · {item.unit}</div>
      <Stepper
        value={item.quantity}
        onDecrement={() => onUpdateQuantity(item.id, item.quantity - 1)}
        onIncrement={() => onUpdateQuantity(item.id, item.quantity + 1)}
      />
    </div>
    <div className="flex-right">
      <div className="cart-item-price">₹{(item.price * item.quantity).toLocaleString()}</div>
      <div className="cart-item-each">₹{item.price} each</div>
      <button className="cart-remove-btn" onClick={() => onRemove(item.id)}>Remove</button>
    </div>
  </div>
);

const Cart = () => {
  const navigate = useNavigate();
  const { items, updateQuantity, removeFromCart, clearCart, totalPrice } = useCart();
  const { user } = useAuth();

  const delivery     = totalPrice >= DELIVERY_THRESHOLD ? 0 : DELIVERY_FEE;
  const orderTotal   = totalPrice + delivery;
  const savingsTotal = items.reduce((s, i) => s + (i.mrp - i.price) * i.quantity, 0);
  const hasRxItems   = items.some(i => i.prescription);
  const rxItemCount  = items.filter(i => i.prescription).length;

  const handleCheckout = () => {
    if (!user) { navigate('/login', { state: { from: '/checkout' } }); return; }
    navigate('/checkout');
  };

  return (
    <div className="page">
      <main className="page-main">

        <div className="breadcrumb">
          <button className="t-link" onClick={() => navigate('/')}>Home</button>
          <span>›</span>
          <span className="bc-current">Cart</span>
        </div>

        <h1 className="t-h1 fade-up page-heading">
          Your Cart
          {items.length > 0 && <span className="page-heading-count">({items.length} item{items.length !== 1 ? 's' : ''})</span>}
        </h1>

        {hasRxItems && items.length > 0 && (
          <div className="strip-warn fade-up mb-24">
            <span className="rx-banner-icon">📋</span>
            <div>
              <div className="rx-banner-title">Prescription required for {rxItemCount} item{rxItemCount !== 1 ? 's' : ''}</div>
              <div className="rx-banner-body">
                You'll be asked to upload a valid doctor's prescription at checkout.
                Items marked <strong>Rx</strong> cannot be dispensed without verification.
              </div>
            </div>
          </div>
        )}

        {items.length === 0 && (
          <div className="empty-state fade-up">
            <div className="empty-icon">🛒</div>
            <p className="empty-title">Your cart is empty</p>
            <p className="empty-body">Add medicines and health products to get started.</p>
            <button className="btn-primary btn-auto" onClick={() => navigate('/services/pharmacy')}>Browse Pharmacy →</button>
          </div>
        )}

        {items.length > 0 && (
          <div className="co-layout fade-up">

            <div className="co-main card">
              <div className="items-header">
                <span className="t-h4">Items</span>
                <button className="btn-clear" onClick={clearCart}>Clear all</button>
              </div>
              {items.map(item => (
                <CartItem key={item.id} item={item} onUpdateQuantity={updateQuantity} onRemove={removeFromCart} />
              ))}
            </div>

            <div className="co-sidebar">
              <div className="card-sticky">
                <div className="summary-heading">Order Summary</div>

                <div className="summary-row">
                  <span className="summary-label">Subtotal</span>
                  <span className="summary-value">₹{totalPrice.toLocaleString()}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Delivery</span>
                  <span className={delivery === 0 ? 'summary-free' : 'summary-value'}>
                    {delivery === 0 ? 'Free' : `₹${delivery}`}
                  </span>
                </div>

                {delivery > 0 && (
                  <div className="delivery-notice">
                    Add ₹{(DELIVERY_THRESHOLD - totalPrice).toLocaleString()} more for free delivery
                  </div>
                )}

                <div className="divider" />

                <div className="summary-total">
                  <span className="t-h4">Total</span>
                  <span className="t-price-lg">₹{orderTotal.toLocaleString()}</span>
                </div>

                {savingsTotal > 0 && (
                  <div className="strip-success mb-16">
                    <span>Total savings</span><span>₹{savingsTotal.toLocaleString()}</span>
                  </div>
                )}

                {hasRxItems && (
                  <div className="strip-info rx-cart-strip">
                    <span>📋</span>
                    <span className="rx-pharmacist">Rx upload required at checkout</span>
                  </div>
                )}

                <button className="btn-primary btn-mb" onClick={handleCheckout}>
                  {user ? 'Proceed to Checkout →' : 'Sign in to Checkout →'}
                </button>
                <button className="btn-outline" onClick={() => navigate('/services/pharmacy')}>Continue Shopping</button>
              </div>
            </div>

          </div>
        )}
      </main>
    </div>
  );
};

export default Cart;
